import { useEffect, useState } from 'react';
import {
  useTranslate,
  useNotification,
  useCustomMutation,
} from '@refinedev/core';
import { Modal, Form, Radio, DatePicker, Descriptions, Tag } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { extractBackendMessage } from '@/utils/apiError';
import type {
  ContractSignIn,
  ContractTerminateIn,
  AppAdminContentCatalogSchemasPublisherContractContractOut as ContractOut,
} from '@/api/generated/schemas';
import { ContractKind } from '@/api/generated/schemas';

const KINDS = Object.values(ContractKind);

type SignValues = {
  kind: ContractKind;
  effective_from: Dayjs;
};

type TerminateValues = {
  effective_to: Dayjs;
};

interface ContractFormModalProps {
  open: boolean;
  onClose: () => void;
  publisherId: number;
  /** When set, the modal terminates this contract; otherwise it signs a new one. */
  contract?: ContractOut | null;
  /** Called after a contract is successfully signed or terminated (e.g. to refetch the list). */
  onSaved?: () => void;
}

export const ContractFormModal = ({
  open,
  onClose,
  publisherId,
  contract,
  onSaved,
}: ContractFormModalProps) => {
  const t = useTranslate();
  const { open: notify } = useNotification();
  const { mutateAsync } = useCustomMutation();
  const [form] = Form.useForm<SignValues & TerminateValues>();
  const [submitting, setSubmitting] = useState(false);

  const isTerminate = !!contract;
  const mode = isTerminate ? 'terminate' : 'sign';

  const tk = (suffix: string) =>
    t(`content_catalog.publishers.contracts.${mode}.${suffix}`);

  useEffect(() => {
    if (open) {
      form.resetFields();
    } else {
      setSubmitting(false);
    }
  }, [open, contract, form]);

  const submitSign = async () => {
    const values = await form.validateFields();
    const payload: ContractSignIn = {
      kind: values.kind,
      effective_from: values.effective_from.toISOString(),
    };
    await mutateAsync({
      url: `content_catalog/publishers/${publisherId}/contracts`,
      method: 'post',
      values: payload,
      successNotification: false,
      errorNotification: false,
    });
  };

  const submitTerminate = async () => {
    const values = await form.validateFields();
    const payload: ContractTerminateIn = {
      effective_to: values.effective_to.toISOString(),
    };
    await mutateAsync({
      url: `content_catalog/publishers/${publisherId}/contracts/${contract!.id}/terminate`,
      method: 'post',
      values: payload,
      successNotification: false,
      errorNotification: false,
    });
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }

    setSubmitting(true);
    try {
      if (isTerminate) {
        await submitTerminate();
      } else {
        await submitSign();
      }

      notify?.({
        type: 'success',
        message: t('notifications.success'),
        description: tk('success'),
      });
      onSaved?.();
      onClose();
    } catch (error) {
      const description = extractBackendMessage(error) ?? tk('error_fallback');
      notify?.({
        type: 'error',
        message: t('notifications.error'),
        description,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={tk('title')}
      okText={tk('submit')}
      okButtonProps={{ loading: submitting, danger: isTerminate }}
      cancelButtonProps={{ disabled: submitting }}
      closable={!submitting}
      maskClosable={!submitting}
      onCancel={onClose}
      onOk={handleSubmit}
      destroyOnClose
      width={520}
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        disabled={submitting}
      >
        {isTerminate ? (
          <>
            <Descriptions size="small" column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item
                label={t('content_catalog.publishers.contracts.fields.kind')}
              >
                <Tag>
                  {t(
                    `content_catalog.publishers.contracts.kind.${contract.kind}`,
                    { defaultValue: contract.kind }
                  )}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item
                label={t(
                  'content_catalog.publishers.contracts.fields.effective_from'
                )}
              >
                {dayjs(contract.effective_from).format('ll')}
              </Descriptions.Item>
            </Descriptions>

            <Form.Item
              name="effective_to"
              label={t(
                'content_catalog.publishers.contracts.fields.effective_to'
              )}
              rules={[
                { required: true, message: tk('errors.effective_to_required') },
                {
                  validator: (_, value: Dayjs | undefined) => {
                    if (!value) return Promise.resolve();
                    const from = dayjs(contract.effective_from);
                    if (!value.isAfter(from)) {
                      return Promise.reject(new Error(tk('errors.date_order')));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              extra={tk('hints.effective_to')}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </>
        ) : (
          <>
            <Form.Item
              name="kind"
              label={t('content_catalog.publishers.contracts.fields.kind')}
              rules={[{ required: true, message: tk('errors.kind_required') }]}
            >
              <Radio.Group
                optionType="button"
                options={KINDS.map((value) => ({
                  label: t(
                    `content_catalog.publishers.contracts.kind.${value}`,
                    { defaultValue: value }
                  ),
                  value,
                }))}
              />
            </Form.Item>

            <Form.Item
              name="effective_from"
              label={t(
                'content_catalog.publishers.contracts.fields.effective_from'
              )}
              rules={[
                {
                  required: true,
                  message: tk('errors.effective_from_required'),
                },
              ]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
};
